import { useState, useEffect } from "react";
import { supabase } from "./supabaseClient";

const fontLink = document.createElement("link");
fontLink.rel = "stylesheet";
fontLink.href = "https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600;700;800&family=DM+Mono:wght@400;500;600&display=swap";
document.head.appendChild(fontLink);

function GridBackground() {
  return (
    <div style={{ position:"fixed", inset:0, overflow:"hidden", zIndex:0 }}>
      <div style={{ position:"absolute", inset:0, background:"#060A10" }} />
      <svg style={{ position:"absolute", inset:0, width:"100%", height:"100%", opacity:0.06 }}>
        <defs>
          <pattern id="grid" width="60" height="60" patternUnits="userSpaceOnUse">
            <path d="M 60 0 L 0 0 0 60" fill="none" stroke="#38BDF8" strokeWidth="0.5"/>
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid)" />
      </svg>
      <div style={{ position:"absolute", top:-200, left:-200, width:600, height:600, borderRadius:"50%", background:"radial-gradient(circle, #38BDF822 0%, transparent 70%)", animation:"drift1 8s ease-in-out infinite" }} />
      <div style={{ position:"absolute", bottom:-300, right:-200, width:700, height:700, borderRadius:"50%", background:"radial-gradient(circle, #0284C718 0%, transparent 70%)", animation:"drift2 10s ease-in-out infinite" }} />
      <style>{`
        @keyframes drift1 { 0%,100%{transform:translate(0,0)} 50%{transform:translate(40px,30px)} }
        @keyframes drift2 { 0%,100%{transform:translate(0,0)} 50%{transform:translate(-30px,-40px)} }
        @keyframes fadeUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
        @keyframes scanline { 0%{top:-4px} 100%{top:100%} }
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }
        @keyframes spinRing { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes spinRingR { from{transform:rotate(0deg)} to{transform:rotate(-360deg)} }
        @keyframes shake { 0%,100%{transform:translateX(0)} 20%{transform:translateX(-8px)} 40%{transform:translateX(8px)} 60%{transform:translateX(-5px)} 80%{transform:translateX(5px)} }
      `}</style>
    </div>
  );
}

export default function LoginPage({ onLogin }) {
  const [email, setEmail]           = useState("");
  const [password, setPassword]     = useState("");
  const [showPass, setShowPass]     = useState(false);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState("");
  const [shake, setShake]           = useState(false);
  const [success, setSuccess]       = useState(false);
  const [focused, setFocused]       = useState(null);
  const [mounted, setMounted]       = useState(false);
  const [mode, setMode]             = useState("login"); // "login" | "reset"
  const [resetSent, setResetSent]   = useState(false);

  useEffect(() => { setTimeout(() => setMounted(true), 80); }, []);

  const triggerError = (msg) => {
    setError(msg); setShake(true);
    setTimeout(() => setShake(false), 600);
  };

  const handleLogin = async (e) => {
    e?.preventDefault();
    if (!email || !password) { triggerError("Enter your email and password."); return; }
    setLoading(true); setError("");

    try {
      const { data, error: authErr } = await supabase.auth.signInWithPassword({ email, password });
      if (authErr) throw authErr;

      const user = data.user;

      // Fetch role from team_members if exists
      const { data: tm } = await supabase
        .from("team_members")
        .select("role, team_id")
        .eq("user_id", user.id)
        .eq("status", "active")
        .maybeSingle();

      // Fetch subscription tier
      const { data: sub } = await supabase
        .from("subscriptions")
        .select("tier, status, trial_end")
        .eq("user_id", user.id)
        .maybeSingle();

      // Check if owner (has a team)
      const { data: ownedTeam } = await supabase
        .from("teams")
        .select("id")
        .eq("owner_id", user.id)
        .maybeSingle();

      const role = ownedTeam ? "owner" : (tm?.role || "member");

      const userData = {
        id:       user.id,
        email:    user.email,
        name:     user.user_metadata?.full_name || user.email.split("@")[0],
        role,
        team_id:  tm?.team_id || ownedTeam?.id || null,
        org_id:   user.id,
        tier:     sub?.tier || "starter",
      };

      setSuccess(true);
      await new Promise(r => setTimeout(r, 700));
      onLogin?.(userData);
    } catch (err) {
      setLoading(false);
      triggerError(err.message || "Invalid email or password.");
    }
  };

  const handleReset = async () => {
    if (!email) { triggerError("Enter your email address first."); return; }
    setLoading(true); setError("");
    try {
      const { error: resetErr } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (resetErr) throw resetErr;
      setResetSent(true);
    } catch (err) {
      triggerError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = (name) => ({
    width:"100%", boxSizing:"border-box",
    background: focused===name ? "#0F1923" : "#080D14",
    border:`1px solid ${focused===name ? "#38BDF8" : error ? "#F8717133" : "#1E293B"}`,
    borderRadius:10, padding:"14px 16px",
    color:"#F1F5F9", fontSize:14, fontFamily:"'DM Sans',sans-serif",
    outline:"none", transition:"all .2s",
    boxShadow: focused===name ? "0 0 0 3px #38BDF811" : "none",
  });

  return (
    <div style={{ position:"relative", minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'DM Sans',sans-serif" }}>
      <GridBackground />

      <div style={{
        position:"relative", zIndex:10,
        width:"100%", maxWidth:420,
        animation: mounted ? "fadeUp .5s ease forwards" : "none",
        opacity: mounted ? 1 : 0,
        margin:"0 16px",
      }}>
        {/* Logo */}
        <div style={{ textAlign:"center", marginBottom:36 }}>
          <div style={{ position:"relative", width:72, height:72, margin:"0 auto 16px" }}>
            <div style={{ position:"absolute", inset:0, borderRadius:"50%", border:"2px solid #38BDF833", animation:"spinRing 6s linear infinite" }} />
            <div style={{ position:"absolute", inset:6, borderRadius:"50%", border:"1px dashed #38BDF822", animation:"spinRingR 4s linear infinite" }} />
            <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center" }}>
              <div style={{ width:36, height:36, borderRadius:8, background:"linear-gradient(135deg,#38BDF8,#0284C7)", display:"flex", alignItems:"center", justifyContent:"center" }}>
                <span style={{ fontSize:16, fontWeight:900, color:"#060A10", fontFamily:"'Bebas Neue',display" }}>W</span>
              </div>
            </div>
          </div>
          <div style={{ fontFamily:"'Bebas Neue',display", fontSize:34, letterSpacing:4, color:"#F1F5F9", lineHeight:1 }}>
            WHOLESALE<span style={{ color:"#38BDF8" }}>IQ</span>
          </div>
          <div style={{ fontSize:11, letterSpacing:3, color:"#334155", fontFamily:"'DM Mono',monospace", marginTop:6 }}>
            SECURE OPERATOR ACCESS
          </div>
        </div>

        {/* Card */}
        <div style={{
          background:"#0D1117",
          border:`1px solid ${error && shake ? "#F8717155" : "#1E293B"}`,
          borderRadius:16, padding:32,
          boxShadow:"0 24px 60px #00000066",
          animation: shake ? "shake .5s ease" : "none",
          position:"relative", overflow:"hidden",
          transition:"border-color .2s",
        }}>
          <div style={{ position:"absolute", left:0, right:0, height:2, background:"linear-gradient(to right, transparent, #38BDF808, transparent)", animation:"scanline 3s linear infinite", pointerEvents:"none" }} />

          {success ? (
            <div style={{ textAlign:"center", padding:"24px 0" }}>
              <div style={{ fontSize:40, marginBottom:12 }}>✓</div>
              <div style={{ fontSize:16, fontWeight:700, color:"#4ADE80" }}>Access Granted</div>
              <div style={{ fontSize:12, color:"#475569", marginTop:6 }}>Loading your dashboard...</div>
              <div style={{ marginTop:20, display:"flex", justifyContent:"center", gap:6 }}>
                {[0,1,2].map(i=>(
                  <div key={i} style={{ width:6, height:6, borderRadius:"50%", background:"#38BDF8", animation:`blink 1s ${i*.2}s infinite` }} />
                ))}
              </div>
            </div>

          ) : resetSent ? (
            <div style={{ textAlign:"center", padding:"24px 0" }}>
              <div style={{ fontSize:36, marginBottom:12 }}>📧</div>
              <div style={{ fontSize:16, fontWeight:700, color:"#38BDF8" }}>Reset Email Sent</div>
              <div style={{ fontSize:13, color:"#475569", marginTop:8 }}>Check your inbox for a password reset link.</div>
              <button onClick={() => { setMode("login"); setResetSent(false); }} style={{ marginTop:20, background:"none", border:"1px solid #1E293B", borderRadius:8, padding:"10px 20px", color:"#475569", cursor:"pointer", fontSize:13 }}>
                Back to Sign In
              </button>
            </div>

          ) : mode === "reset" ? (
            <>
              <div style={{ fontSize:18, fontWeight:800, color:"#F1F5F9", marginBottom:6 }}>Reset Password</div>
              <div style={{ fontSize:13, color:"#475569", marginBottom:28 }}>Enter your email and we'll send a reset link.</div>
              <div style={{ marginBottom:20 }}>
                <label style={{ display:"block", fontSize:11, fontFamily:"'DM Mono',monospace", color:"#64748B", letterSpacing:1, marginBottom:7 }}>EMAIL ADDRESS</label>
                <input type="email" value={email} onChange={e=>{setEmail(e.target.value);setError("");}}
                  onFocus={()=>setFocused("email")} onBlur={()=>setFocused(null)}
                  placeholder="you@company.com" style={inputStyle("email")} autoComplete="email" />
              </div>
              {error && (
                <div style={{ background:"#F8717111", border:"1px solid #F8717133", borderRadius:8, padding:"10px 14px", marginBottom:20, display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ color:"#F87171", fontSize:13 }}>⚠ {error}</span>
                </div>
              )}
              <button onClick={handleReset} disabled={loading} style={{ width:"100%", border:"none", borderRadius:10, background: loading ? "#1E293B" : "linear-gradient(135deg,#38BDF8,#0284C7)", color: loading ? "#475569" : "#060A10", padding:"14px", fontSize:13, fontWeight:800, cursor: loading?"not-allowed":"pointer", fontFamily:"'DM Mono',monospace", letterSpacing:2 }}>
                {loading ? "SENDING..." : "SEND RESET LINK →"}
              </button>
              <button onClick={() => { setMode("login"); setError(""); }} style={{ width:"100%", marginTop:12, background:"none", border:"none", color:"#475569", fontSize:13, cursor:"pointer", padding:8 }}>
                Back to Sign In
              </button>
            </>

          ) : (
            <>
              <div style={{ fontSize:18, fontWeight:800, color:"#F1F5F9", marginBottom:6 }}>Sign in</div>
              <div style={{ fontSize:13, color:"#475569", marginBottom:28 }}>Access your wholesale pipeline</div>

              <div style={{ marginBottom:16 }}>
                <label style={{ display:"block", fontSize:11, fontFamily:"'DM Mono',monospace", color:"#64748B", letterSpacing:1, marginBottom:7 }}>EMAIL ADDRESS</label>
                <input type="email" value={email} onChange={e=>{setEmail(e.target.value);setError("");}}
                  onFocus={()=>setFocused("email")} onBlur={()=>setFocused(null)}
                  onKeyDown={e=>e.key==="Enter"&&handleLogin()}
                  placeholder="you@company.com" style={inputStyle("email")} autoComplete="email" />
              </div>

              <div style={{ marginBottom:8 }}>
                <label style={{ display:"block", fontSize:11, fontFamily:"'DM Mono',monospace", color:"#64748B", letterSpacing:1, marginBottom:7 }}>PASSWORD</label>
                <div style={{ position:"relative" }}>
                  <input type={showPass?"text":"password"} value={password}
                    onChange={e=>{setPassword(e.target.value);setError("");}}
                    onFocus={()=>setFocused("password")} onBlur={()=>setFocused(null)}
                    onKeyDown={e=>e.key==="Enter"&&handleLogin()}
                    placeholder="••••••••••"
                    style={{ ...inputStyle("password"), paddingRight:48 }}
                    autoComplete="current-password" />
                  <button onClick={()=>setShowPass(!showPass)} style={{ position:"absolute", right:14, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", color:"#475569", fontSize:14, padding:4 }}>
                    {showPass ? "🙈" : "👁"}
                  </button>
                </div>
              </div>

              <div style={{ textAlign:"right", marginBottom:20 }}>
                <button onClick={()=>{ setMode("reset"); setError(""); }} style={{ background:"none", border:"none", color:"#38BDF8", fontSize:12, cursor:"pointer", padding:"4px 0", fontFamily:"'DM Mono',monospace" }}>
                  Forgot password?
                </button>
              </div>

              {error && (
                <div style={{ background:"#F8717111", border:"1px solid #F8717133", borderRadius:8, padding:"10px 14px", marginBottom:20, display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ color:"#F87171", fontSize:13 }}>⚠ {error}</span>
                </div>
              )}

              <button onClick={handleLogin} disabled={loading} style={{
                width:"100%", border:"none", borderRadius:10,
                background: loading ? "#1E293B" : "linear-gradient(135deg, #38BDF8 0%, #0284C7 100%)",
                color: loading ? "#475569" : "#060A10",
                padding:"14px", fontSize:13, fontWeight:800, cursor: loading?"not-allowed":"pointer",
                fontFamily:"'DM Mono',monospace", letterSpacing:2, transition:"all .2s",
                boxShadow: loading ? "none" : "0 4px 20px #38BDF833",
              }}>
                {loading ? (
                  <span style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
                    <span style={{ display:"inline-block", width:12, height:12, border:"2px solid #475569", borderTopColor:"#38BDF8", borderRadius:"50%", animation:"spinRing .7s linear infinite" }} />
                    AUTHENTICATING...
                  </span>
                ) : "ACCESS DASHBOARD →"}
              </button>
            </>
          )}
        </div>

        <div style={{ textAlign:"center", marginTop:20, fontSize:11, color:"#1E293B", fontFamily:"'DM Mono',monospace" }}>
          WHOLESALEIQ · SECURE · ENCRYPTED
        </div>
      </div>
    </div>
  );
}
